import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, TrendingUp, AlertTriangle, Flame, Plus, Trash2, Download, FileText, Copy, Activity, Mail, Settings, Lock, Sparkles, Target, BarChart3, Pin, Gauge } from "lucide-react";
import { Button, Card, Badge, ProgressTrack, Metric, Logo } from "../components/kit";
import api, { API, mediaUrl } from "../lib/api";
import { dueInfo } from "../lib/challenge";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ShareWin } from "../components/ShareWin";
import { UsageMeter, LimitNotice } from "../components/CoachPlan";
import { LOCKED_COPY } from "../lib/plans";
import { Layers, ShieldCheck, ClipboardList } from "lucide-react";

const STATUS = { active: { tone: "good", label: "Active" }, idle: { tone: "watch", label: "Idle" }, at_risk: { tone: "risk", label: "At risk" }, locked: { tone: "risk", label: "Locked" }, invited: { tone: "neutral", label: "Invited" } };
const DEFAULT_ORDER = ["cohort_overview", "top_constraint", "health_bands", "stage_dist", "roster", "challenges", "custom_metrics"];
const BANDS = [["At risk (0-39)", "risk"], ["Building (40-59)", "watch"], ["Healthy (60-74)", "brand"], ["Strong (75-100)", "good"]];

export default function CoachDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [trustNeeded, setTrustNeeded] = useState(false);
  const [cohort, setCohort] = useState("");
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const load = (c = cohort) => api.get("/coach/overview", { params: c ? { cohort: c } : {} }).then((r) => {
    if (!r.data.profile?.onboarded) { navigate(r.data.is_owner === false ? "/" : "/coach/onboarding"); return; }
    setData(r.data);
  }).catch((e) => {
    if (e.response?.data?.detail === "trust_ack_required") { setTrustNeeded(true); return; }
    setError(e.response?.status === 403 ? (e.response?.data?.detail === "Coach access only." ? "This area is for business coaches." : e.response?.data?.detail) : "Could not load your cohort.");
  });

  useEffect(() => { load(); }, []); // eslint-disable-line

  if (trustNeeded) return <TeamTrustGate onDone={() => { setTrustNeeded(false); load(); }} />;
  if (error) return <Wrap><p style={{ color: "var(--status-risk)" }} data-testid="coach-error">{error}</p><Button variant="secondary" size="sm" onClick={() => navigate("/")} style={{ marginTop: 16 }}>Back home</Button></Wrap>;
  if (!data) return <Wrap><p style={{ color: "var(--text-muted)" }}>Loading your cohort…</p></Wrap>;

  const { branding, is_pro: isPro, pinned_card: pinned, features = {}, is_owner: isOwner } = data;
  const accent = branding?.accent_color || "#106958";
  const exportUrl = `${API}/coach/export.csv${cohort ? "" : ""}`;
  const startUpgrade = () => (isOwner ? navigate("/coach/settings#plan") : toast.info("Ask your account owner to upgrade the plan."));
  const pickCohort = (c) => { setCohort(c); load(c); };
  const copyCode = () => { navigator.clipboard?.writeText(data.invite_code); setCopied(true); setTimeout(() => setCopied(false), 1600); };

  const order = [...(data.layout_order || []), ...DEFAULT_ORDER.filter((s) => !(data.layout_order || []).includes(s))];
  const sectionProps = { data, isPro, features, startUpgrade, accent, load, navigate };

  return (
    <div className="buktu-root" data-brand="catalyst" data-testid="coach-page" style={{ minHeight: "100vh", background: "var(--surface-page)", "--catalyst-accent": accent }}>
      <div className="buktu-wrap" style={{ maxWidth: 1080, paddingTop: "var(--space-8)", paddingBottom: "var(--space-20)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-6)" }}>
          {branding?.logo_url ? <img src={mediaUrl(branding.logo_url)} alt={data.cohort_name} data-testid="coach-brand-logo" style={{ height: 32, maxWidth: 180, objectFit: "contain" }} /> : <Logo height={28} to="catalyst" />}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {isOwner && <Button variant="secondary" size="sm" data-testid="coach-settings-btn" iconLeft={<Settings size={15} />} onClick={() => navigate("/coach/settings")}>Settings</Button>}
            {isOwner && <Button variant="secondary" size="sm" data-testid="coach-manage-btn" onClick={() => navigate("/coach/settings#plan")}>Plan</Button>}
            <Button variant="secondary" size="sm" iconLeft={features.exports ? <Download size={15} /> : <Lock size={14} />} data-testid="coach-export-csv" onClick={() => features.exports ? (window.location.href = exportUrl) : startUpgrade()}>Export</Button>
            <Button variant="secondary" size="sm" iconLeft={features.exports ? <FileText size={15} /> : <Lock size={14} />} data-testid="coach-report-btn" onClick={() => features.exports ? navigate("/coach/report") : startUpgrade()}>Report</Button>
            <Button variant="ghost" size="sm" data-testid="coach-logout-btn" onClick={async () => { await logout(); navigate("/"); }}>Log out</Button>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "var(--space-4)", marginBottom: "var(--space-6)" }}>
          <div>
            <span className="buktu-eyebrow" style={{ color: accent }}>Catalyst · coach cohort</span>
            <h1 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)" }}>{data.cohort_name}</h1>
            <p style={{ font: "var(--type-body)", color: "var(--text-muted)" }}>{data.benchmarks.cohort_size} businesses · {data.benchmarks.scored_count} with a baseline</p>
          </div>
          <Card padding="var(--space-4) var(--space-5)" data-testid="coach-invite-code" style={{ display: "flex", alignItems: "center", gap: "var(--space-4)", flexWrap: "wrap" }}>
            <div><span className="buktu-eyebrow">Invite code</span><div style={{ fontFamily: "var(--font-mono)", fontSize: 20, fontWeight: 700, color: accent, letterSpacing: "0.06em" }}>{data.invite_code}</div></div>
            <Button size="sm" variant="secondary" iconLeft={<Copy size={14} />} onClick={copyCode} data-testid="coach-copy-code">{copied ? "Copied" : "Copy"}</Button>
          </Card>
        </div>

        <Card padding="var(--space-5)" data-testid="coach-plan-usage" style={{ marginBottom: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)", flexWrap: "wrap" }}>
            <Badge tone="accent" dot data-testid="coach-tier-badge">{data.tier_name}</Badge>
            {data.acting_member && <Badge tone="neutral" data-testid="coach-team-badge">Team member · {data.acting_member.name}</Badge>}
            <div style={{ flex: 1, minWidth: 220 }}><UsageMeter label="Active members (last 30 days)" used={data.usage.active_members} limit={data.usage.limit} testid="coach-active-members" /></div>
            {data.tier !== "catalyst_custom" && <Button size="sm" variant={data.tier === "catalyst_free" ? "primary" : "secondary"} data-testid="coach-upgrade-btn" iconLeft={<Sparkles size={14} />} onClick={startUpgrade}>Upgrade</Button>}
          </div>
          <LimitNotice ls={data.usage} testid="coach-limit-notice" />
          {data.pending_join_count > 0 && <p data-testid="coach-pending-joins" style={{ font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-muted)" }}>{data.pending_join_count} business owner{data.pending_join_count > 1 ? "s are" : " is"} waiting to join your program. They already use Vitals for free; upgrade to add them to your dashboard.</p>}
        </Card>

        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-5)", flexWrap: "wrap" }}>
          <Layers size={16} style={{ color: accent }} />
          <span style={{ font: "var(--type-label)" }}>Viewing</span>
          <select className="buktu-input" data-testid="coach-cohort-filter" value={cohort} onChange={(e) => pickCohort(e.target.value)} style={{ maxWidth: 280 }}>
            <option value="">All members</option>
            {(data.cohorts || []).map((c) => <option key={c.id} value={c.id}>{c.name} · {c.members}</option>)}
          </select>
          {isOwner && <Button size="sm" variant="ghost" data-testid="coach-manage-cohorts" onClick={() => navigate("/coach/settings")}>Manage cohorts</Button>}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
          {order.map((key) => <Section key={key} id={key} pinned={pinned === key} {...sectionProps} />)}
          {/* Fixed, always-accessible */}
          <AtRiskCard atRisk={data.at_risk} pinned={pinned === "at_risk"} accent={accent} />
          {features.benchmarks ? <BenchmarksCard data={data} accent={accent} /> : <LockedCard id="benchmarks" accent={accent} onUpgrade={startUpgrade} />}
          {features.team_view ? <TeamViewCard rows={data.team_view || []} accent={accent} /> : <LockedCard id="team_view" accent={accent} onUpgrade={startUpgrade} />}
          {features.outcomes ? <OutcomesCard accent={accent} /> : <LockedCard id="outcomes" accent={accent} onUpgrade={startUpgrade} cta="Contact us" onCta={() => navigate("/pricing")} />}
        </div>
      </div>
    </div>
  );
}

function LockedCard({ id, accent, onUpgrade, cta, onCta }) {
  const c = LOCKED_COPY[id];
  return (
    <Card padding="var(--space-6)" data-testid={`locked-${id}`} style={{ background: "var(--surface-brand-wash)", border: "1px dashed var(--border-strong)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <span style={{ width: 40, height: 40, borderRadius: "var(--radius-md)", background: "var(--paper-white)", display: "grid", placeItems: "center", color: accent, flexShrink: 0 }}><Lock size={18} /></span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ font: "var(--type-heading)", color: "var(--text-strong)" }}>{c.title} <Badge tone="neutral">{c.tier}</Badge></div>
          <div style={{ font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>{c.desc}</div>
        </div>
        <Button size="sm" data-testid={`locked-${id}-upgrade`} onClick={onCta || onUpgrade}>{cta || "Upgrade"}</Button>
      </div>
    </Card>
  );
}

function TeamViewCard({ rows, accent }) {
  return (
    <Shell testid="section-team_view" title="Team dashboard · all cohorts" icon={<Layers size={20} />} accent={accent}>
      <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: -8, marginBottom: 12 }}>Aggregates only. Averages and constraints appear once a cohort has at least 3 members.</p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", font: "var(--type-body-sm)", fontSize: 13 }}>
          <thead><tr style={{ textAlign: "left", color: "var(--text-muted)" }}>{["Cohort", "Members", "Active", "Onboarded", "Avg score", "Top constraint"].map((h) => <th key={h} style={{ padding: "6px 8px", fontWeight: 600 }}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`team-view-row-${r.id}`} style={{ borderTop: "1px solid var(--border-hairline)" }}>
                <td style={{ padding: "8px", color: "var(--text-strong)" }}>{r.name}</td><td style={{ padding: 8 }}>{r.members}</td><td style={{ padding: 8 }}>{r.active}</td>
                <td style={{ padding: 8 }}>{r.pct_onboarded}%</td><td style={{ padding: 8 }}>{r.avg_score ?? "—"}</td><td style={{ padding: 8 }}>{r.top_constraint || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Shell>
  );
}

function OutcomesCard({ accent }) {
  return (
    <Shell testid="section-outcomes" title="Referral & outcomes tracking" icon={<ClipboardList size={20} />} accent={accent}>
      <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>Referral and outcomes tracking and grant-style reporting are configured with your Buktu onboarding team. Reach out to your onboarding contact to adjust what's tracked.</p>
    </Shell>
  );
}

function TeamTrustGate({ onDone }) {
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const confirm = async () => {
    setBusy(true);
    try { const { data } = await api.post("/coach/team/trust-ack"); setUser(data); onDone(); } catch { toast.error("Couldn't save — please try again."); } finally { setBusy(false); }
  };
  return (
    <Wrap>
      <Card padding="var(--space-8)" data-testid="team-trust-gate" style={{ maxWidth: 560, textAlign: "left" }}>
        <h2 style={{ font: "var(--type-title)", fontSize: 24, marginBottom: 6 }}>How member privacy works</h2>
        <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", marginBottom: "var(--space-5)" }}>Please read before continuing.</p>
        <div style={{ padding: "var(--space-5)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)", display: "flex", gap: 12 }}>
          <span style={{ color: "var(--buktu-green)", flexShrink: 0 }}><ShieldCheck size={22} /></span>
          <div style={{ font: "var(--type-body-sm)", color: "var(--text-body)", lineHeight: 1.6 }}>
            Your members control what you can see. By default you see <b>aggregate, anonymized patterns</b> across your cohort — not individual numbers. An individual's detail only appears to you if <b>that specific member has explicitly turned sharing on</b>. We never show a person's raw financial or sentiment data without their own opt-in.
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: "var(--space-5)", cursor: "pointer", font: "var(--type-body-sm)", color: "var(--text-strong)" }}>
          <input data-testid="team-trust-check" type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} />
          I've read and understand how member privacy works.
        </label>
        <Button data-testid="team-trust-continue" disabled={!ok || busy} onClick={confirm} style={{ marginTop: "var(--space-5)" }}>{busy ? "Saving…" : "Continue to dashboard"}</Button>
      </Card>
    </Wrap>
  );
}

function Shell({ title, icon, pinned, accent, children, testid }) {
  return (
    <Card padding="var(--space-6)" data-testid={testid} style={pinned ? { border: `2px solid ${accent}`, boxShadow: "var(--shadow-md)" } : undefined}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-4)" }}>
        <span style={{ color: accent }}>{icon}</span>
        <h3 style={{ font: "var(--type-heading)", flex: 1 }}>{title}</h3>
        {pinned && <Badge tone="accent"><Pin size={11} style={{ marginRight: 4 }} />Pinned</Badge>}
      </div>
      {children}
    </Card>
  );
}

function Section({ id, pinned, data, isPro, features, startUpgrade, accent, load, navigate }) {
  if (LOCKED_COPY[id] && !features[id]) return <LockedCard id={id} accent={accent} onUpgrade={startUpgrade} />;
  if (id === "cohort_overview") {
    const o = data.cohort_overview;
    return (
      <Shell testid="section-cohort_overview" title="Cohort overview" icon={<Users size={20} />} pinned={pinned} accent={accent}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "var(--space-4)" }}>
          <Metric label="Active members" value={o.stated_count ?? o.members_total} note={`${o.members_total} tracked`} />
          <Metric label="Completed onboarding" value={`${o.pct_onboarded}%`} />
          <Metric label="With a live streak" value={`${o.pct_streak}%`} />
        </div>
      </Shell>
    );
  }
  if (id === "health_bands") {
    const b = data.health_bands; const total = Object.values(b).reduce((a, c) => a + c, 0) || 1;
    return (
      <Shell testid="section-health_bands" title="Business Health distribution" icon={<Gauge size={20} />} pinned={pinned} accent={accent}>
        <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: -8, marginBottom: 12 }}>Score bands across the cohort — not individual scores.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {BANDS.map(([label, tone]) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 130, font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-body)" }}>{label}</span>
              <div style={{ flex: 1 }}><ProgressTrack value={Math.round(100 * b[label] / total)} tone={tone} height={10} /></div>
              <span style={{ width: 28, textAlign: "right", font: "var(--type-data)", fontSize: 13 }}>{b[label]}</span>
            </div>
          ))}
        </div>
      </Shell>
    );
  }
  if (id === "top_constraint") {
    const t = data.top_constraint;
    return (
      <Shell testid="section-top_constraint" title="Top constraint" icon={<Target size={20} />} pinned={pinned} accent={accent}>
        {!t ? <p style={{ font: "var(--type-body-sm)", color: "var(--text-faint)" }}>No constraint data yet.</p> : (
          <div>
            <p style={{ font: "var(--type-body)", color: "var(--text-strong)" }}><b style={{ fontSize: 22, color: accent }}>{t.pct}%</b> say their biggest blocker right now is <b>{t.label}</b>.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              {Object.entries(t.breakdown).sort((a, c) => c[1] - a[1]).map(([k, n]) => <Badge key={k} tone={k === t.label ? "accent" : "neutral"}>{k} · {n}</Badge>)}
            </div>
          </div>
        )}
      </Shell>
    );
  }
  if (id === "stage_dist") {
    const s = data.stage_dist; const total = s.total || 1;
    const trend = data.stage_trend || [];
    const first = trend[0]?.counts, last = trend[trend.length - 1]?.counts;
    const delta = (st) => (first && last ? (last[st] ?? 0) - (first[st] ?? 0) : 0);
    return (
      <Shell testid="section-stage_dist" title="Hustler → Founder → CEO" icon={<TrendingUp size={20} />} pinned={pinned} accent={accent}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {["Hustler", "Founder", "CEO"].map((st) => {
            const d = delta(st);
            return (
            <div key={st} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 70, font: "var(--type-body-sm)", fontSize: 13 }}>{st}</span>
              <div style={{ flex: 1 }}><ProgressTrack value={Math.round(100 * s.counts[st] / total)} tone="brand" height={10} /></div>
              <span style={{ width: 28, textAlign: "right", font: "var(--type-data)", fontSize: 13 }}>{s.counts[st]}</span>
              {trend.length > 1 && <span data-testid={`stage-delta-${st}`} style={{ width: 42, textAlign: "right", font: "var(--type-data)", fontSize: 12, color: d > 0 ? "var(--status-good)" : d < 0 ? "var(--status-risk)" : "var(--text-faint)" }}>{d > 0 ? `▲${d}` : d < 0 ? `▼${Math.abs(d)}` : "–"}</span>}
            </div>
          );})}
        </div>
        {trend.length > 1 && <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: 10 }}>Movement over the last {trend.length} weeks.</p>}
      </Shell>
    );
  }
  if (id === "roster") return <RosterCard roster={data.roster} pinned={pinned} accent={accent} />;
  if (id === "challenges") return <ChallengesCard challenges={data.challenges} dimensions={data.dimensions_order} streakBoard={data.streak_leaderboard} onChange={load} canAdd={isPro} onUpgrade={startUpgrade} pinned={pinned} accent={accent} branding={data.branding} cohortName={data.cohort_name} />;
  if (id === "custom_metrics") return <CustomMetricsCard metrics={data.custom_metrics} trends={data.metric_trends} canAdd={isPro} onChange={load} onUpgrade={startUpgrade} pinned={pinned} accent={accent} branding={data.branding} cohortName={data.cohort_name} />;
  return null;
}

function RosterCard({ roster, pinned, accent }) {
  return (
    <Shell testid="section-roster" title="Roster" icon={<Users size={20} />} pinned={pinned} accent={accent}>
      <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: -8, marginBottom: 12 }}>Only fields each member has chosen to share are shown.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }} data-testid="coach-roster">
        {roster.length === 0 && <p data-testid="coach-roster-empty" style={{ font: "var(--type-body-sm)", color: "var(--text-faint)" }}>No business owners here yet. Share your invite code to bring members in.</p>}
        {roster.map((m) => (
          <div key={m.id} data-testid={`roster-${m.id}`} style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-hairline)" }}>
            <div style={{ width: 36, height: 36, borderRadius: 999, background: "var(--surface-brand-wash)", display: "grid", placeItems: "center", font: "var(--type-label)", fontWeight: 700, color: accent, flexShrink: 0 }}>{m.name.slice(0, 1).toUpperCase()}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ font: "var(--type-label)", color: "var(--text-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</div>
              <div style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)" }} data-testid={`roster-shared-${m.id}`}>{m.shared_count} of {m.shared_total} fields shared</div>
              {m.custom_values && m.custom_values.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }} data-testid={`roster-custom-${m.id}`}>
                  {m.custom_values.map((cv, i) => <Badge key={i} tone="accent">{cv.label}: {cv.value}</Badge>)}
                </div>
              )}
              {m.on_a_roll && (
                <div style={{ marginTop: 4 }}>
                  <Badge tone="good" dot data-testid={`roster-roll-${m.id}`}><Flame size={11} style={{ marginRight: 3 }} />On a roll · {m.roll_weeks}w</Badge>
                </div>
              )}
            </div>
            {m.streak > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3, font: "var(--type-data)", fontSize: 12, color: "var(--status-watch)" }}><Flame size={13} />{m.streak}</span>}
            {m.overall !== null ? <span style={{ width: 34, textAlign: "right", font: "var(--type-data)", fontSize: 13 }}>{m.overall}</span> : <span style={{ font: "var(--type-body-sm)", fontSize: 11, color: "var(--text-faint)" }}>private</span>}
            <Badge tone={STATUS[m.status].tone} dot>{STATUS[m.status].label}</Badge>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function CustomMetricsCard({ metrics, trends, canAdd, onChange, onUpgrade, pinned, accent, branding, cohortName }) {
  const [label, setLabel] = useState(""); const [type, setType] = useState("number"); const [options, setOptions] = useState(""); const [target, setTarget] = useState(""); const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!label.trim()) return; setSaving(true);
    try {
      const payload = { label, type };
      if (type === "select") payload.options = options.split(",").map((s) => s.trim()).filter(Boolean);
      if ((type === "number" || type === "yesno") && target !== "") payload.target = Number(target);
      await api.post("/coach/custom-metrics", payload);
      setLabel(""); setType("number"); setOptions(""); setTarget(""); onChange(); toast.success("Metric added");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add."); } finally { setSaving(false); }
  };
  const remove = async (id) => { await api.delete(`/coach/custom-metrics/${id}`); onChange(); };
  const TYPE_LABEL = { number: "Number", yesno: "Yes / No", text: "Text", date: "Date", select: "Dropdown" };
  const goals = (metrics || []).filter((m) => m.target != null && trends?.[m.id]?.length).map((m) => {
    const s = trends[m.id]; const cur = s[s.length - 1].value; const pct = m.type === "yesno";
    const progress = Math.max(0, Math.min(100, Math.round((100 * cur) / (m.target || 1))));
    return { id: m.id, label: m.label, cur, target: m.target, progress, pct };
  });
  return (
    <Shell testid="section-custom_metrics" title="Custom metrics (Pro)" icon={<BarChart3 size={20} />} pinned={pinned} accent={accent}>
      <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: -8, marginBottom: 12 }}>Track a field beyond the baseline. Members always see these clearly labeled as shared with you before they answer.</p>
      {goals.length > 0 && (
        <div data-testid="goal-progress" style={{ marginBottom: "var(--space-4)", display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ font: "var(--type-label)", fontSize: 12, color: "var(--text-strong)" }}>Goal progress</span>
          {goals.map((g) => (
            <div key={g.id} data-testid={`goal-${g.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, font: "var(--type-body-sm)", fontSize: 13, marginBottom: 4 }}>
                <Target size={13} style={{ color: accent }} />
                <span style={{ flex: 1, color: "var(--text-strong)" }}>{g.label}</span>
                <span style={{ font: "var(--type-data)", fontSize: 12, color: "var(--text-body)" }}>{g.pct ? `${Math.round(g.cur)}%` : g.cur} / {g.pct ? `${Math.round(g.target)}%` : g.target}</span>
                <ShareWin testid={`share-goal-${g.id}`} kind="goal" accent={accent} logoUrl={branding?.logo_url} cohortName={cohortName} payload={g} />
              </div>
              <ProgressTrack value={g.progress} tone={g.progress >= 100 ? "good" : "brand"} height={8} />
            </div>
          ))}
        </div>
      )}
      {canAdd ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: "var(--space-4)" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input className="buktu-input" data-testid="metric-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Weekly revenue goal" style={{ flex: 2, minWidth: 160 }} />
            <select className="buktu-input" data-testid="metric-type" value={type} onChange={(e) => setType(e.target.value)} style={{ flex: 1, minWidth: 120 }}>
              {Object.entries(TYPE_LABEL).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
            <Button size="sm" data-testid="metric-add" onClick={add} disabled={saving} iconLeft={<Plus size={14} />}>Add</Button>
          </div>
          {type === "select" && <input className="buktu-input" data-testid="metric-options" value={options} onChange={(e) => setOptions(e.target.value)} placeholder="Dropdown options, comma separated (e.g. Red, Amber, Green)" />}
          {(type === "number" || type === "yesno") && <input className="buktu-input" data-testid="metric-target" type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={type === "yesno" ? "Target % answering Yes (optional)" : "Target value (optional)"} />}
        </div>
      ) : (
        <Button size="sm" variant="secondary" iconLeft={<Lock size={14} />} onClick={onUpgrade} style={{ marginBottom: "var(--space-4)" }}>Upgrade to add metrics</Button>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        {(!metrics || metrics.length === 0) && <p style={{ font: "var(--type-body-sm)", color: "var(--text-faint)" }}>No custom metrics yet.</p>}
        {(metrics || []).map((m) => (
          <div key={m.id} data-testid={`metric-${m.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "var(--space-3)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-hairline)" }}>
            <span style={{ flex: 1, font: "var(--type-label)", color: "var(--text-strong)" }}>{m.label}{m.type === "select" && m.options?.length ? <span style={{ font: "var(--type-body-sm)", fontSize: 11, color: "var(--text-faint)", marginLeft: 8 }}>{m.options.join(" · ")}</span> : null}</span>
            {trends?.[m.id]?.length > 1 && <MetricTrend series={trends[m.id]} target={m.target} accent={accent} />}
            {canAdd && (m.type === "number" || m.type === "yesno") && <TargetEditor metric={m} onSaved={onChange} />}
            <Badge tone="neutral">{TYPE_LABEL[m.type] || m.type}</Badge>
            {canAdd && <button onClick={() => remove(m.id)} data-testid={`metric-delete-${m.id}`} style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--text-faint)" }}><Trash2 size={16} /></button>}
          </div>
        ))}
      </div>
    </Shell>
  );
}

function TargetEditor({ metric, onSaved }) {
  const [val, setVal] = useState(metric.target ?? "");
  const [editing, setEditing] = useState(false);
  const save = async () => {
    setEditing(false);
    const t = val === "" ? null : Number(val);
    if (t === (metric.target ?? null)) return;
    try { await api.patch(`/coach/custom-metrics/${metric.id}`, { target: t }); toast.success("Target updated"); onSaved(); }
    catch { toast.error("Couldn't set target."); }
  };
  return editing ? (
    <input autoFocus data-testid={`metric-target-edit-${metric.id}`} className="buktu-input" style={{ width: 90 }} type="number" value={val} onChange={(e) => setVal(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && save()} />
  ) : (
    <button type="button" data-testid={`metric-target-btn-${metric.id}`} onClick={() => setEditing(true)} title="Set target" style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "1px solid var(--border-hairline)", background: "transparent", borderRadius: 999, padding: "3px 10px", cursor: "pointer", font: "var(--type-data)", fontSize: 12, color: metric.target != null ? "var(--buktu-green-deep)" : "var(--text-faint)" }}><Target size={12} />{metric.target != null ? `${metric.target}` : "set target"}</button>
  );
}

function MetricTrend({ series, target, accent }) {
  const vals = series.map((s) => s.value);
  const last = vals[vals.length - 1], prev = vals.length > 1 ? vals[vals.length - 2] : last;
  const delta = Math.round((last - prev) * 100) / 100;
  const isPct = series[series.length - 1].type === "yesno";
  const hasTarget = target != null && !isNaN(Number(target));
  const allv = hasTarget ? [...vals, Number(target)] : vals;
  const min = Math.min(...allv), max = Math.max(...allv), range = Math.max(1, max - min);
  const w = 64, h = 20;
  const y = (v) => h - ((v - min) / range) * h;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${y(v)}`).join(" ");
  const fmt = (n) => (isPct ? `${Math.round(n)}%` : n);
  return (
    <span data-testid="metric-trend" title="Cohort trend — change vs last week" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <svg width={w} height={h} style={{ display: "block" }}>
        {hasTarget && <line x1="0" y1={y(Number(target))} x2={w} y2={y(Number(target))} stroke="var(--text-faint)" strokeWidth="1" strokeDasharray="3 2" />}
        <polyline points={pts} fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span style={{ font: "var(--type-data)", fontSize: 12, color: "var(--text-body)" }}>{fmt(last)}</span>
      <span style={{ font: "var(--type-data)", fontSize: 11, color: delta > 0 ? "var(--status-good)" : delta < 0 ? "var(--status-risk)" : "var(--text-faint)" }}>{delta > 0 ? `▲${fmt(Math.abs(delta))}` : delta < 0 ? `▼${fmt(Math.abs(delta))}` : "–"}</span>
      {hasTarget && <span data-testid="metric-goal" title="Target" style={{ font: "var(--type-data)", fontSize: 11, color: "var(--text-faint)" }}>&#127919; {fmt(Number(target))}</span>}
    </span>
  );
}

function ChallengesCard({ challenges, dimensions, streakBoard, onChange, canAdd, onUpgrade, pinned, accent, branding, cohortName }) {
  const [adding, setAdding] = useState(false); const [title, setTitle] = useState(""); const [desc, setDesc] = useState(""); const [dim, setDim] = useState(""); const [due, setDue] = useState(""); const [saving, setSaving] = useState(false);
  const create = async () => { if (!title.trim()) return; setSaving(true); try { await api.post("/coach/challenges", { title, description: desc, dimension: dim, due_date: due }); setTitle(""); setDesc(""); setDim(""); setDue(""); setAdding(false); onChange(); } finally { setSaving(false); } };
  const remove = async (id) => { await api.delete(`/coach/challenges/${id}`); onChange(); };
  return (
    <Shell testid="section-challenges" title="Challenges (Pro)" icon={<Flame size={20} />} pinned={pinned} accent={accent}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -46, marginBottom: 12 }}>
        <Button size="sm" variant="secondary" iconLeft={canAdd ? <Plus size={14} /> : <Lock size={14} />} data-testid="coach-add-challenge" onClick={() => canAdd ? setAdding((a) => !a) : onUpgrade()}>New</Button>
      </div>
      {adding && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", marginBottom: "var(--space-4)", padding: "var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)" }}>
          <input className="buktu-input" data-testid="challenge-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Challenge title" />
          <input className="buktu-input" data-testid="challenge-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Short description (optional)" />
          <select className="buktu-input" data-testid="challenge-dimension" value={dim} onChange={(e) => setDim(e.target.value)}><option value="">Any dimension</option>{dimensions.map((d) => <option key={d} value={d}>{d}</option>)}</select>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-muted)" }}>Due date (optional)<input className="buktu-input" data-testid="challenge-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} /></label>
          <Button size="sm" data-testid="challenge-save" onClick={create} disabled={saving}>{saving ? "Saving…" : "Add challenge"}</Button>
        </div>
      )}
      {streakBoard && streakBoard.length > 0 && (
        <div data-testid="streak-leaderboard" style={{ marginBottom: "var(--space-4)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}><Flame size={15} style={{ color: "var(--status-watch)" }} /><span style={{ font: "var(--type-label)", color: "var(--text-strong)", flex: 1 }}>Streak leaderboard</span><ShareWin testid="share-streak" kind="streak" accent={accent} logoUrl={branding?.logo_url} cohortName={cohortName} payload={{ rows: streakBoard }} /></div>
          {streakBoard.slice(0, 6).map((r, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, font: "var(--type-body-sm)", fontSize: 13, padding: "3px 0" }}>
              <span style={{ width: 18, color: "var(--text-faint)" }}>{i + 1}</span>
              <span style={{ flex: 1, color: r.anonymous ? "var(--text-muted)" : "var(--text-strong)", fontStyle: r.anonymous ? "italic" : "normal" }}>{r.name}</span>
              <Badge tone="watch" dot>{r.streak}w</Badge>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {challenges.length === 0 && <p style={{ font: "var(--type-body-sm)", color: "var(--text-faint)" }}>No challenges yet. Leaderboards stay anonymized unless a member opts in.</p>}
        {challenges.map((c) => (
          <div key={c.id} data-testid={`challenge-${c.id}`} style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-hairline)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
              <div style={{ flex: 1 }}>
                <div style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{c.title}</div>
                {c.description && <div style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-muted)" }}>{c.description}</div>}
                <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                  {c.dimension && <Badge tone="accent" dot>{c.dimension}</Badge>}
                  <Badge tone="neutral">{c.enrolled ?? 0} enrolled</Badge>
                  <Badge tone="good">{c.completed_count ?? 0} completed</Badge>
                  {(() => { const di = dueInfo(c.due_date); return di ? <Badge tone={di.tone} dot data-testid={`challenge-due-${c.id}`}>{di.label}</Badge> : null; })()}
                </div>
              </div>
              <button onClick={() => remove(c.id)} data-testid={`challenge-delete-${c.id}`} style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--text-faint)" }}><Trash2 size={16} /></button>
            </div>
            {c.leaderboard && c.leaderboard.length > 0 && (
              <div data-testid={`leaderboard-${c.id}`} style={{ marginTop: 10, borderTop: "1px solid var(--border-hairline)", paddingTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                {c.leaderboard.map((e, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, font: "var(--type-body-sm)", fontSize: 13 }}>
                    <span style={{ width: 20, color: "var(--text-faint)" }}>{i + 1}</span>
                    <span style={{ flex: 1, color: e.anonymous ? "var(--text-muted)" : "var(--text-strong)", fontStyle: e.anonymous ? "italic" : "normal" }}>{e.name}</span>
                    {e.completed ? <Badge tone="good" dot>Done</Badge> : <Badge tone="neutral">In progress</Badge>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </Shell>
  );
}

function AtRiskCard({ atRisk, pinned, accent }) {
  const rows = atRisk || [];
  return (
    <Shell testid="coach-atrisk" title={`At-risk this week${rows.length ? ` · ${rows.length}` : ""}`} icon={<AlertTriangle size={18} />} pinned={pinned} accent={accent}>
      {rows.length === 0 ? <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>Everyone's on track.</p> : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {rows.map((r) => (
            <div key={r.id} data-testid={`atrisk-${r.id}`} style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", background: "var(--status-risk-soft)" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: "var(--type-label)", color: "#8a2a1a" }}>{r.name}{r.business ? ` · ${r.business}` : ""}</div>
                <div style={{ font: "var(--type-body-sm)", fontSize: 12, color: "#8a2a1a" }}>{r.reason}</div>
              </div>
              {r.overall !== null && r.overall !== undefined && <span style={{ font: "var(--type-data)", fontSize: 13, color: "#8a2a1a" }}>{r.overall}</span>}
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}

function BenchmarksCard({ data, accent }) {
  const b = data.benchmarks;
  return (
    <Shell testid="coach-benchmarks" title="Benchmarks vs the Buktu community" icon={<TrendingUp size={18} />} accent={accent}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "var(--space-4)" }} className="buktu-coach-metrics">
        <Metric label="Cohort average" value={b.avg_overall} unit="/100" />
        <Metric label="Median" value={b.median} unit="/100" />
        <Metric label="Active" value={b.active_count} note="checked in recently" />
        <Metric label="At risk" value={b.at_risk_count} note="score under 45" />
      </div>
    </Shell>
  );
}

function Wrap({ children }) {
  return <div className="buktu-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center", textAlign: "center", padding: 24 }}><div>{children}</div></div>;
}
